/**
 * A cookbook: recipes you want to keep, each a title, a note, a link and the dish photo.
 *
 * Deliberately not a recipe *manager* — no ingredients, no steps, no scaling. The link is
 * the source of truth; this is the shelf you put it on so you can find it again.
 *
 * PURE: no storage, no clock beyond what is passed in.
 */

export interface Recipe {
  id: string
  title: string
  description: string
  /** The page the recipe came from. Optional: a family recipe has no link. */
  url: string
  /** Hotlinked, not copied. A dead link degrades to a placeholder rather than a broken app. */
  imageUrl: string
  createdAt: string
}

export interface CookbookState {
  recipes: Recipe[]
}

export const MAX_TITLE = 120
export const MAX_DESCRIPTION = 600

export function emptyCookbook(): CookbookState {
  return { recipes: [] }
}

/** Newest first: the one you just added is the one you are about to cook. */
export function sortedRecipes(state: CookbookState): Recipe[] {
  return [...state.recipes].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/**
 * Only https, and only something that parses. The image is hotlinked into an <img> and the
 * link is opened in a new tab, so a javascript: or data: URL here would be a real problem.
 */
export function safeUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return null
  }
  return parsed.protocol === 'https:' ? parsed.toString() : null
}

/** The site a recipe came from, for a one-word source label under the title. */
export function sourceLabel(url: string): string | null {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '')
    return host || null
  } catch {
    return null
  }
}
